/**
 * Locale-aware formatting of the numbers shown to users. Every helper reads
 * the active Lingui locale when it is called, so none of them can run at
 * module level: the locale is activated by the entrypoint before any
 * component renders (see `i18n.ts`).
 *
 * Values that feed form inputs or are stored are not formatted here: a number
 * input takes a dot as its decimal separator whatever the locale, so those
 * sites keep `toFixed`.
 */
import { i18n } from '@lingui/core'
import { formatDefaultLocale } from 'd3'
import type { FormatLocaleDefinition } from 'd3'

/**
 * d3's number locale for each locale that has a catalog in `i18n.ts`, used
 * by chart axes and color legends.
 */
const d3Locales: Record<string, FormatLocaleDefinition> = {
  en: { decimal: '.', thousands: ',', grouping: [3], currency: ['$', ''] },
  es: { decimal: ',', thousands: '.', grouping: [3], currency: ['', ' €'] },
}

/**
 * The locale numbers are formatted in: the active Lingui locale, with
 * English for the pseudo-locale and for any locale without an entry above.
 */
export function formattingLocale(): string {
  const locale = i18n.locale
  return locale in d3Locales ? locale : 'en'
}

/**
 * Points d3's default number format at the active locale. Called by
 * `activateLocale` once the Lingui locale is active.
 */
export function activateFormatting(): void {
  formatDefaultLocale(d3Locales[formattingLocale()])
}

export type NumberFormatOptions = Intl.NumberFormatOptions & {
  /** A fixed number of fraction digits, like `toFixed` */
  decimals?: number
}

const numberFormatters = new Map<string, Intl.NumberFormat>()

/**
 * An `Intl.NumberFormat` for the active locale. Instances are cached, so the
 * same options return the same instance for as long as the locale is active.
 */
export function numberFormatter(
  options: NumberFormatOptions = {}
): Intl.NumberFormat {
  const { decimals, ...intlOptions } = options
  if (decimals != null) {
    intlOptions.minimumFractionDigits = decimals
    intlOptions.maximumFractionDigits = decimals
  }
  const locale = formattingLocale()
  const key = `${locale}:${JSON.stringify(intlOptions)}`
  let formatter = numberFormatters.get(key)
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, intlOptions)
    numberFormatters.set(key, formatter)
  }
  return formatter
}

/**
 * `value` as text in the active locale, with the locale's decimal and
 * thousands separators. Without options, up to three fraction digits.
 */
export function formatNumber(
  value: number,
  options?: NumberFormatOptions
): string {
  return numberFormatter(options).format(value)
}

/**
 * A ratio as a percentage in the active locale: 0.125 is "12.5%" in English
 * and "12,5 %" in Spanish. Without options, no fraction digits.
 */
export function formatPercent(
  ratio: number,
  options?: NumberFormatOptions
): string {
  return numberFormatter({ ...options, style: 'percent' }).format(ratio)
}
